import { AttributesMixin, define, Model } from "@type-r/models";
import { isEmpty } from "lodash";
import { ComputedField, Mapping, StaticField } from "../import-templates/models/import-template-details";
import { AssetTypeItem } from "./assetTypes";
import { getAuthorizationBearer, getPlatformCode, restfulIO } from "./endpoint";

export interface ImportTemplateUpdate extends AttributesMixin<typeof ImportTemplateUpdate> { }

@define
export class ImportTemplateUpdate extends Model {
  static endpoint = restfulIO('api/v1/ingest/import-templates');

  static attributes = {
    id: String,
  };

  appendFromDataMappings({ formData, items }: { formData: FormData, items: Mapping[] }) {
    items.forEach((item, index) => {
      formData.append(`mappings[${index}].assetTypeId`, `${item.assetTypeId}`),
      formData.append(`mappings[${index}].validationRuleId`, `${item.validationRuleId ? item.validationRuleId : 0 }`),
      item.mappingItems.length > 0 ?
          item.mappingItems.forEach((mappingItem, indexMapping) => {
            formData.append(`mappings[${index}].mappingItems[${indexMapping}].from.name`, mappingItem.from.name),
              formData.append(`mappings[${index}].mappingItems[${indexMapping}].from.x`, mappingItem.from.x.toString()),
              formData.append(`mappings[${index}].mappingItems[${indexMapping}].from.y`, mappingItem.from.y.toString()),
              formData.append(`mappings[${index}].mappingItems[${indexMapping}].from.isGlobal`, mappingItem.from.isGlobal ? 'True' : 'False'),
              mappingItem.from.globalValuePosition ? formData.append(`mappings[${index}].mappingItems[${indexMapping}].from.globalValuePosition`, mappingItem.from.globalValuePosition) : null,
              mappingItem.from.formatting ? formData.append(`mappings[${index}].mappingItems[${indexMapping}].from.formatting.name`, mappingItem.from.formatting.name) : null,
              mappingItem.from.formatting ? formData.append(`mappings[${index}].mappingItems[${indexMapping}].from.formatting.value`, mappingItem.from.formatting.value) : null,
              formData.append(`mappings[${index}].mappingItems[${indexMapping}].to.id`, mappingItem.to.id),
              formData.append(`mappings[${index}].mappingItems[${indexMapping}].to.name`, mappingItem.to.name),
              formData.append(`mappings[${index}].mappingItems[${indexMapping}].to.targetFieldTypeId`, mappingItem.to.targetFieldTypeId.toString()),
              formData.append(`mappings[${index}].mappingItems[${indexMapping}].isChild`, mappingItem.isChild ? 'True' : 'False')
          })
          : formData.append(`mappings[${index}].mappingItems`, '[]')
    })
  }

  appendFromDataStaticFields({ formData, items }: { formData: FormData, items: StaticField[] }) {
    items.forEach((item, index) => {
      formData.append(`staticFields[${index}].assetTypeId`, `${item.assetTypeId}`),
        item.staticFieldItems.forEach((staticItem, indexStatic) => {
          formData.append(`staticFields[${index}].staticFieldItems[${indexStatic}].fieldId`, staticItem.fieldId.toString()),
            formData.append(`staticFields[${index}].staticFieldItems[${indexStatic}].fieldName`, staticItem.fieldName),
            formData.append(`staticFields[${index}].staticFieldItems[${indexStatic}].value`, staticItem.value)
        })
    })
  }

  appendFromDataComputedFields({ formData, items }: { formData: FormData, items: ComputedField[] }) {
    items.forEach((item, index) => {
      formData.append(`computedFields[${index}].assetTypeId`, `${item.assetTypeId}`),
        item.computedFieldItems.forEach((computedItem, indexComputed) => {
          formData.append(`computedFields[${index}].computedFieldItems[${indexComputed}].field1.id`, computedItem.field1.id.toString()),
            formData.append(`computedFields[${index}].computedFieldItems[${indexComputed}].field1.name`, computedItem.field1.name.toString()),
            formData.append(`computedFields[${index}].computedFieldItems[${indexComputed}].field2.id`, computedItem.field2.id.toString()),
            formData.append(`computedFields[${index}].computedFieldItems[${indexComputed}].field2.name`, computedItem.field2.name.toString()),
            formData.append(`computedFields[${index}].computedFieldItems[${indexComputed}].operator`, computedItem.operator),
            formData.append(`computedFields[${index}].computedFieldItems[${indexComputed}].resultField.id`, computedItem.resultField.id.toString()),
            formData.append(`computedFields[${index}].computedFieldItems[${indexComputed}].resultField.name`, computedItem.resultField.name.toString())
        })
    })
  }

  appendFromDataCompanies({ formData, items }: { formData: FormData, items: { id: string, name: string }[] }) {
    items.forEach((item, index) => (
      formData.append(`companies[${index}].id`, item.id),
      formData.append(`companies[${index}].name`, item.name)
    ));
  }

  appendFromDataAssetTypes({ formData, items }: { formData: FormData, items: { id: string, name: string }[] }) {
    items.forEach((item, index) => (
      formData.append(`assettypes[${index}].id`, item.id),
      formData.append(`assettypes[${index}].name`, item.name)
    ));
  }

  appendFromDataCriteria({ formData, items }: { formData: FormData, items: { assetType: AssetTypeItem, searchValue: string }[] }) {
    items.forEach((item, index) => (
      formData.append(`assetFilterCriteria.criteria[${index}].assetType.id`, item.assetType.id),
      formData.append(`assetFilterCriteria.criteria[${index}].assetType.name`, item.assetType.name),
      formData.append(`assetFilterCriteria.criteria[${index}].searchValue`, item.searchValue)
    ));
  }

  appendFromDataAssetFilterCriteria({ formData, item }: { formData: FormData, item }) {
    if (!isEmpty(item) && item.filterColumn !== null) {
      formData.append('assetFilterCriteria.filterColumn.name', item.filterColumn.name);
      formData.append('assetFilterCriteria.filterColumn.x', item.filterColumn.x);
      formData.append('assetFilterCriteria.filterColumn.y', item.filterColumn.y);
      this.appendFromDataCriteria({ formData: formData, items: item.criteria });
    }
  }

  async put({ id, name, assetTypes, isSingleAsset, isDefault, isActive, metadata, mappings, staticFields, computedFields, companies, omitUnmappedFields, delimiter, assetFilterCriteria, workSheet }) {
    const url = (this.getEndpoint() as any).getUrl(this);
    const formData = new FormData();
    formData.append('id', id);
    formData.append('name', name);
    formData.append('isSingleAsset', isSingleAsset);
    formData.append('isActive', isActive ? 'True' : 'False');
    formData.append('isDefault', isDefault ? 'True' : 'False');
    formData.append('metadata.dateFormat', metadata.dateFormat);
    formData.append('delimiter', delimiter);
    formData.append('omitUnmappedFields', omitUnmappedFields);
    formData.append('workSheet.id', workSheet.id);
    formData.append('workSheet.name', workSheet.name);
    this.appendFromDataAssetFilterCriteria({ formData: formData, item: assetFilterCriteria });
    this.appendFromDataMappings({ formData: formData, items: mappings });
    this.appendFromDataStaticFields({ formData: formData, items: staticFields });
    this.appendFromDataComputedFields({ formData: formData, items: computedFields });
    this.appendFromDataCompanies({ formData: formData, items: companies });
    this.appendFromDataAssetTypes({ formData: formData, items: assetTypes });

    const response = await fetch(url, {
      method: 'PUT',
      headers: new Headers({
        "Authorization": getAuthorizationBearer(),
        'X-Platform-Code': getPlatformCode()
      }),
      body: formData
    });

    if (response.status < 200 || response.status >= 300) {
      let error = response.statusText || `HTTP Error ${response.status}`;
      await response.json()
        .then(e => error = Object.keys(e.errors).map(k => e.errors[k].join(' ')).join(' '))
        .catch(e => console.error('Cannot parse response data'));
      throw new Error(error);
    }

    const json = await response.json();
    this.set(json);
    return this;
  }
}